/**
 * A06 — Bilan Comptable: FE form ↔ BE situation_bilan column mapping.
 * Writable columns only (excludes id, created_at, updated_at).
 */

import { getBrandDefaultCurrency } from '../../../shared/utils/brandCurrency';

export const BILAN_WRITABLE_FIELDS = [
  'bilan_ref',
  'exercice_fiscal',
  'periode_debut',
  'periode_fin',
  'date_etablissement',
  'date_cloture',
  'statut_bilan',
  'type_bilan',
  'devise',
  'taux_conversion_eur',
  'actif_immobilise_brut',
  'actif_immobilise_amortissement',
  'actif_immobilise_net',
  'actif_circulant_brut',
  'actif_circulant_provision',
  'actif_circulant_net',
  'actif_tresorerie',
  'total_actif',
  'capitaux_propres_capital',
  'capitaux_propres_reserves',
  'capitaux_propres_resultat',
  'capitaux_propres_total',
  'passif_dettes_long_terme',
  'passif_dettes_court_terme',
  'passif_provisions',
  'passif_total',
  'total_passif',
  'equilibre_actif_passif',
  'fonds_roulement',
  'besoin_fonds_roulement',
  'tresorerie_nette',
  'ratio_liquidite',
  'ratio_solvabilite',
  'ratio_autonomie',
  'commentaires_actif',
  'commentaires_passif',
  'commentaires_capitaux',
  'annexes_references',
  'notes_explicatives',
  'auditeur_nom',
  'auditeur_date',
  'auditeur_certification',
  'validation_daf',
  'validation_dg',
  'validation_cab',
  'comparatif_exercice_precedent',
  'variation_actif',
  'variation_passif',
  'retraitements_appliques',
  'ajustements',
  'conformite_pcn',
  'conformite_ifrs',
  'conformite_fiscale',
  'publication_requise',
  'publication_date',
  'publication_url',
  'archivage_comptable',
  'archivage_path',
  'notes',
  'metadata',
  'created_by',
];

const DATE_FIELDS = new Set([
  'periode_debut',
  'periode_fin',
  'date_etablissement',
  'date_cloture',
  'auditeur_date',
  'publication_date',
]);

const DECIMAL_FIELDS = new Set([
  'taux_conversion_eur',
  'actif_immobilise_brut',
  'actif_immobilise_amortissement',
  'actif_immobilise_net',
  'actif_circulant_brut',
  'actif_circulant_provision',
  'actif_circulant_net',
  'actif_tresorerie',
  'total_actif',
  'capitaux_propres_capital',
  'capitaux_propres_reserves',
  'capitaux_propres_resultat',
  'capitaux_propres_total',
  'passif_dettes_long_terme',
  'passif_dettes_court_terme',
  'passif_provisions',
  'passif_total',
  'total_passif',
  'fonds_roulement',
  'besoin_fonds_roulement',
  'tresorerie_nette',
  'ratio_liquidite',
  'ratio_solvabilite',
  'ratio_autonomie',
  'variation_actif',
  'variation_passif',
]);

const INTEGER_FIELDS = new Set([
  'equilibre_actif_passif',
  'validation_daf',
  'validation_dg',
  'validation_cab',
  'conformite_pcn',
  'conformite_ifrs',
  'conformite_fiscale',
  'publication_requise',
  'archivage_comptable',
]);

const INTEGER_DEFAULTS = Object.freeze({
  equilibre_actif_passif: 1,
  validation_daf: 0,
  validation_dg: 0,
  validation_cab: 0,
  conformite_pcn: 1,
  conformite_ifrs: 0,
  conformite_fiscale: 1,
  publication_requise: 0,
  archivage_comptable: 0,
});

export const EMPTY_BILAN_FORM = Object.freeze({
  ...BILAN_WRITABLE_FIELDS.reduce((acc, field) => {
    acc[field] = INTEGER_FIELDS.has(field) ? INTEGER_DEFAULTS[field] : '';
    return acc;
  }, {}),
  devise: getBrandDefaultCurrency(),
});

const normalizeDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  return String(value);
};

const toFormScalar = (field, value) => {
  if (value == null) {
    return INTEGER_FIELDS.has(field) ? INTEGER_DEFAULTS[field] : '';
  }
  if (DATE_FIELDS.has(field)) return normalizeDate(value);
  if (INTEGER_FIELDS.has(field)) return value === 1 || value === true ? 1 : 0;
  return value;
};

/** Map API row → form state (all writable columns). */
export const recordToFormData = (record = {}) => {
  const form = { ...EMPTY_BILAN_FORM };
  BILAN_WRITABLE_FIELDS.forEach((field) => {
    if (record[field] !== undefined && record[field] !== null) {
      form[field] = toFormScalar(field, record[field]);
    }
  });
  return form;
};

/** Map form state → API payload (typed for situation_bilan). */
export const buildBilanPayload = (formData = {}) => {
  const payload = {};

  BILAN_WRITABLE_FIELDS.forEach((field) => {
    const raw = formData[field];

    if (DECIMAL_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? 0 : parseFloat(raw) || 0;
      return;
    }

    if (INTEGER_FIELDS.has(field)) {
      if (raw === '' || raw == null) {
        payload[field] = INTEGER_DEFAULTS[field];
        return;
      }
      payload[field] = raw === true || raw === 1 || raw === '1' ? 1 : 0;
      return;
    }

    if (DATE_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? null : normalizeDate(raw);
      return;
    }

    payload[field] = raw === '' || raw == null ? null : raw;
  });

  return payload;
};
